import React, { useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "../ui/dialog";
import { Input } from "../ui/input";
import { Textarea } from "../ui/textarea";
import { Button } from "../ui/button";
import { Label } from "../ui/label";
import { toast } from "sonner";
import { submitConsultation } from "../../lib/api";
import { CalendarClock, Loader2 } from "lucide-react";

export default function BookConsultationModal({ open, onOpenChange }) {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "", preferred_date: "", preferred_time: "", topic: "", message: "" });

  const onChange = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) {
      toast.error("Name and email are required");
      return;
    }
    setLoading(true);
    try {
      const { data } = await submitConsultation(form);
      toast.success(data.message);
      onOpenChange(false);
      setForm({ name: "", email: "", phone: "", company: "", preferred_date: "", preferred_time: "", topic: "", message: "" });
    } catch (err) {
      toast.error("Something went wrong. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="book-consultation-modal">
        <DialogHeader>
          <div className="inline-flex items-center gap-2 text-primary text-sm font-semibold">
            <CalendarClock className="h-4 w-4" /> Book a Consultation
          </div>
          <DialogTitle className="text-2xl">Talk to a senior security consultant</DialogTitle>
          <DialogDescription>
            A 45-minute strategy session &mdash; no cost, no obligation. Share a few details and we&apos;ll confirm within one business day.
          </DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-3 pt-2">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <Label htmlFor="bc-name">Full name*</Label>
              <Input id="bc-name" data-testid="bc-name-input" value={form.name} onChange={onChange("name")} required />
            </div>
            <div>
              <Label htmlFor="bc-email">Work email*</Label>
              <Input id="bc-email" data-testid="bc-email-input" type="email" value={form.email} onChange={onChange("email")} required />
            </div>
            <div>
              <Label htmlFor="bc-phone">Phone</Label>
              <Input id="bc-phone" data-testid="bc-phone-input" value={form.phone} onChange={onChange("phone")} />
            </div>
            <div>
              <Label htmlFor="bc-company">Company</Label>
              <Input id="bc-company" data-testid="bc-company-input" value={form.company} onChange={onChange("company")} />
            </div>
            <div>
              <Label htmlFor="bc-date">Preferred date</Label>
              <Input id="bc-date" data-testid="bc-date-input" type="date" value={form.preferred_date} onChange={onChange("preferred_date")} />
            </div>
            <div>
              <Label htmlFor="bc-time">Preferred time</Label>
              <Input id="bc-time" data-testid="bc-time-input" type="time" value={form.preferred_time} onChange={onChange("preferred_time")} />
            </div>
          </div>
          <div>
            <Label htmlFor="bc-topic">Topic</Label>
            <Input id="bc-topic" data-testid="bc-topic-input" placeholder="e.g. ISO 27001 readiness" value={form.topic} onChange={onChange("topic")} />
          </div>
          <div>
            <Label htmlFor="bc-msg">Anything specific to discuss?</Label>
            <Textarea id="bc-msg" data-testid="bc-message-input" rows={3} value={form.message} onChange={onChange("message")} />
          </div>
          <Button
            type="submit"
            disabled={loading}
            data-testid="bc-submit-btn"
            className="w-full bg-primary hover:bg-primary/90 rounded-full h-11 text-primary-foreground"
          >
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : "Confirm Booking"}
          </Button>
        </form>
      </DialogContent>
    </Dialog>
  );
}
